import React, { useRef, useState } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Sparkles, Image as ImageIcon, Upload, Loader2, CheckCircle2 } from 'lucide-react';
import { api, apiUpload } from '../../lib/api';
import { errorMessage, useApi } from '../../hooks/useApi';
import type { StudioTitle } from './TitleCatalog';

const inputClass =
  'w-full px-3 py-2 bg-[#171022] border border-[#2c2140] rounded-lg text-sm text-white focus:outline-none focus:border-violet-500';

export function TitleEditorModal({
  open,
  onOpenChange,
  title: existing,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The title being edited, or null to add a new one. */
  title: StudioTitle | null;
  onSaved: () => void;
}) {
  const genresState = useApi<{ genres: { slug: string; label: string }[] }>(open ? '/admin/genres' : null);

  const [title, setTitle] = useState(existing?.title ?? '');
  const [subTitle, setSubTitle] = useState(existing?.subTitle ?? '');
  const [synopsis, setSynopsis] = useState(existing?.synopsis ?? '');
  const [selectedGenres, setSelectedGenres] = useState<string[]>(existing?.genres ?? []);
  const [director, setDirector] = useState(existing?.director ?? '');
  const [cast, setCast] = useState(existing?.cast?.join(', ') ?? '');
  const [releaseYear, setReleaseYear] = useState<number>(existing?.releaseYear ?? new Date().getFullYear());
  const [posterUrl, setPosterUrl] = useState(existing?.posterVerticalUrl ?? '');
  const [themeColor, setThemeColor] = useState(existing?.themeColor ?? '#8b5cf6');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const genres = genresState.data?.genres ?? [];

  const handlePosterFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadError(null);
    setIsUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await apiUpload<{ url: string }>('/admin/uploads/image', form);
      setPosterUrl(res.url);
    } catch (err) {
      setUploadError(errorMessage(err));
    } finally {
      setIsUploading(false);
    }
  };

  // Titles store the readable genre name ("Mystery"), which is what the phone app displays.
  const toggleGenre = (label: string) =>
    setSelectedGenres((prev) => (prev.includes(label) ? prev.filter((g) => g !== label) : [...prev, label]));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const body: Record<string, unknown> = {
        title: title.trim(),
        subTitle: subTitle.trim(),
        synopsis: synopsis.trim(),
        genres: selectedGenres,
        cast: cast
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        director: director.trim(),
        releaseYear: Number(releaseYear),
        themeColor,
      };
      if (existing) body.id = existing.id;
      // Only send a poster when there is one (an empty string is not a valid link).
      if (posterUrl.trim()) body.posterVerticalUrl = posterUrl.trim();
      await api('/admin/titles', { method: 'POST', body });
      onSaved();
      if (existing) onOpenChange(false);
      else setSubmitted(true);
    } catch (err) {
      setSaveError(errorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={existing ? 'Edit title' : 'Add a new title'}
      description="These details are what viewers see in the app."
      maxWidth="3xl"
    >
      {submitted ? (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          <p className="text-sm text-white font-semibold">Submitted for review.</p>
          <p className="text-xs text-gray-400">It goes live after the team approves it.</p>
          <button
            onClick={() => onOpenChange(false)}
            className="mt-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold"
          >
            Done
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">Title *</label>
                <input type="text" required value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">Tagline</label>
                <input type="text" value={subTitle} onChange={(e) => setSubTitle(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">Story summary *</label>
                <textarea
                  required
                  rows={3}
                  value={synopsis}
                  onChange={(e) => setSynopsis(e.target.value)}
                  className={`${inputClass} resize-none`}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1.5">Categories</label>
                {genresState.loading && !genresState.data ? (
                  <p className="text-xs text-gray-500">Loading categories…</p>
                ) : genresState.error && !genresState.data ? (
                  <p className="text-xs text-rose-400">
                    {genresState.error}{' '}
                    <button type="button" onClick={genresState.reload} className="underline">
                      Try again
                    </button>
                  </p>
                ) : genres.length === 0 ? (
                  <p className="text-xs text-gray-500">No categories have been set up yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {genres.map((g) => {
                      const selected = selectedGenres.includes(g.label);
                      return (
                        <button
                          type="button"
                          key={g.slug}
                          onClick={() => toggleGenre(g.label)}
                          aria-pressed={selected}
                          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                            selected
                              ? 'bg-violet-600 text-white shadow-sm'
                              : 'bg-[#171022] border border-[#2c2140] text-gray-400 hover:text-white'
                          }`}
                        >
                          {g.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-300 mb-1">Director</label>
                  <input type="text" value={director} onChange={(e) => setDirector(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-300 mb-1">Release year</label>
                  <input
                    type="number"
                    value={releaseYear}
                    onChange={(e) => setReleaseYear(Number(e.target.value))}
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">Cast (separate names with commas)</label>
                <input type="text" value={cast} onChange={(e) => setCast(e.target.value)} className={inputClass} />
              </div>
            </div>

            <div className="space-y-4">
              <label className="block text-xs font-semibold text-gray-300">Preview</label>
              <div className="relative aspect-[9/16] rounded-xl overflow-hidden border-2 border-[#2c2140] bg-[#0c0814] shadow-lg flex flex-col justify-end p-3">
                {posterUrl ? (
                  <img src={posterUrl} alt="Poster preview" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-gray-500">
                    <ImageIcon className="w-8 h-8" />
                    <span className="text-[10px]">No image yet</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="relative z-10">
                  {selectedGenres[0] && (
                    <span
                      className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase text-white mb-1"
                      style={{ backgroundColor: themeColor }}
                    >
                      {selectedGenres[0]}
                    </span>
                  )}
                  <h4 className="text-white text-xs font-bold leading-tight truncate">{title || 'Your title'}</h4>
                  {synopsis && <p className="text-gray-300 text-[10px] line-clamp-2 mt-0.5">{synopsis}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-semibold text-gray-300">Poster image</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handlePosterFile}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold transition-colors disabled:opacity-50"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Uploading…</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-3.5 h-3.5" />
                      <span>{posterUrl ? 'Replace image' : 'Upload image'}</span>
                    </>
                  )}
                </button>
                {uploadError && <p className="text-[11px] text-rose-400">{uploadError}</p>}
                <details>
                  <summary className="text-[11px] text-gray-400 cursor-pointer hover:text-gray-300 list-none">
                    or paste an image link
                  </summary>
                  {/* Deliberately type="text": an uploaded image can be a relative path, which fails url validation. */}
                  <input
                    type="text"
                    value={posterUrl}
                    onChange={(e) => setPosterUrl(e.target.value)}
                    placeholder="https://…"
                    className="mt-1.5 w-full px-3 py-1.5 bg-[#171022] border border-[#2c2140] rounded-lg text-xs text-white focus:outline-none focus:border-violet-500"
                  />
                </details>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-300 mb-1">Accent colour</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={themeColor}
                    onChange={(e) => setThemeColor(e.target.value)}
                    className="w-8 h-8 rounded-lg cursor-pointer bg-transparent border-0"
                  />
                  <span className="text-xs font-mono text-gray-400">{themeColor}</span>
                </div>
              </div>
            </div>
          </div>

          {!existing && (
            <p className="text-xs text-gray-400">New titles are sent to the team for review before they go live.</p>
          )}
          {saveError && (
            <p className="text-xs text-rose-400" role="alert">
              {saveError}
            </p>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#241b33]">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 rounded-lg text-xs font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || isUploading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold shadow-md shadow-violet-900/30 transition-all disabled:opacity-50"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>{isSaving ? 'Saving…' : existing ? 'Save changes' : 'Submit for review'}</span>
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
